import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { apiClient, BuildingTableLoaderResult } from "~/services/apiClient";
import { LoaderData, FormattedBuildingData, FormattedUnitData } from "~/types";
import { getCachedAuthAccessInfo, loadCookiesData } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export type BuildingTablePageLoaderType = {
  buildingList: LoaderData<FormattedBuildingData[]>;
  isAuthenticated: boolean;
};

export function buildingTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType,
) {
  return ["building-table", ownerID, ownerType] as const;
}

export const buildingTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => buildingTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchBuildingTableData(): Promise<
  BuildingTablePageLoaderType | Response
> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");

  const { ownerID, ownerType } = authAccessInfo.data;

  const result = await apiClient.loader<BuildingTableLoaderResult>(
    "building-table",
    { ownerID, ownerType },
  );

  const buildingListData: FormattedBuildingData[] = (
    result.data?.buildingList ?? []
  ).map(({ buildingDetails, propertyDetails }) => ({
    buildingDetails,
    propertyDetails,
    unitList: [] as FormattedUnitData[],
  }));

  return {
    buildingList: {
      success: result.success,
      data: buildingListData,
    },
    isAuthenticated: true,
  };
}

export async function BuildingTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<BuildingTablePageLoaderType | Response> {
  void request;
  const data = await fetchBuildingTableData();
  if (data instanceof Response) {
    return data;
  }

  const auth = getCachedAuthAccessInfo();
  if (auth) {
    setLoaderQueryData(
      buildingTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  }

  return data;
}
